import axios from "axios";
import React, { Component } from "react";
import {
  PrismBanner,
  PrismButton,
  PrismCheckbox,
  PrismInputText,
  PrismInputTextarea,
} from "@prism-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FileInput } from "../Files";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import PermissionLevel from "../../data/PermissionLevel";
import Unauthorized from "../Unauthorized";
import PageHeader from "../PageHeader";
import Loader from "../Loader";
import { onCheckedChange } from "../../utils/prismCheckbox";
export class ProfileForm extends Component {
  state = {
    loading: true,
    error: null,
    success: false,
    profile: {},
    invalidImageTypeSelected: false,
    officerRoles: [],
    selectedOfficerRoleIds: [],
  };

  componentDidMount() {
    this.loadProfile();
    this.loadOfficerRoles();
  }

  loadProfile() {
    return axios
      .get(`/api/users/${this.props.params.user_id}`)
      .then((response) => {
        const profile = response.data;
        const selectedOfficerRoleIds = (profile.officer_roles || []).map(r => r.id);
        this.setState({
          profile: profile,
          selectedOfficerRoleIds: selectedOfficerRoleIds,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        this.setState({
          loading: false,
          error: "Unable to load profile!",
        });
      });
  }

  loadOfficerRoles() {
    return axios
      .get('/api/officerRoles')
      .then((response) => {
        this.setState({
          officerRoles: response.data,
        });
      })
      .catch((error) => {
        console.error("Unable to load officer roles:", error);
      });
  }

  // Handlers
  handleChange = (e) => {
    this.setProfileField(e.target.name, e.target.value);
  };

  // Prism inputs report their value on `event.detail`, not on a native target,
  // so the field name is passed explicitly.
  handlePrismChange = (name) => (event) => {
    this.setProfileField(name, event.detail?.value ?? '');
  };

  setProfileField = (name, value) => {
    let { profile, error } = this.state;

    if (name !== 'photo') {
      profile[name] = value;
    }

    // reset the error state when a user updates the form
    if (error === "Invalid fields submitted!") {
      error = null;
    }

    this.setState({ profile, error });
  };

  handleOfficerRoleChange = (roleId) => {
    const { selectedOfficerRoleIds } = this.state;
    const isSelected = selectedOfficerRoleIds.includes(roleId);
    
    let newSelectedIds;
    if (isSelected) {
      // Remove the role
      newSelectedIds = selectedOfficerRoleIds.filter(id => id !== roleId);
    } else {
      // Add the role
      newSelectedIds = [...selectedOfficerRoleIds, roleId];
    }
    
    this.setState({ selectedOfficerRoleIds: newSelectedIds });
  };

  invalidFileSelected = (invalidFileSelected) => {
    this.setState({ invalidImageTypeSelected: invalidFileSelected });
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.setState({ success: false, error: null, loading: true });

    // Validate Form
    let form = e.currentTarget;
    let isValid = form.checkValidity() && !this.state.invalidImageTypeSelected;
    if (!isValid) {
      this.setState({ error: "Invalid fields submitted!", loading: false });
      return false;
    }

    // Prepare data to send - include officer role IDs
    const dataToSend = {
      ...this.state.profile,
      officer_role_ids: this.state.selectedOfficerRoleIds,
    };

    // If valid, send to the backend
    axios
      .put(`/api/users/${this.props.params.user_id}`, dataToSend)
      .then((res) => {
        this.setState({ error: null, success: true, loading: false });
        // Embedded in a dialog the banner is never seen -- the host closes on save --
        // so hand the updated record back and let it refresh its own list.
        if (this.props.onSaved) {
          this.props.onSaved(res.data || dataToSend);
        }
      })
      .catch((res) => {
        this.setState({
          error: "Unexpected error! Saving profile failed.",
          success: false,
          loading: false
        });
      });
  };

  // Class property rather than a prototype method: this is passed as a bare callback
  // (`onPrismClick={this.handleCancel}`), which previously worked only because the body
  // never referenced `this`. It does now.
  handleCancel = (e) => {
    // When rendered inside a dialog there is no history entry to go back to --
    // the host opened the form in place, so closing is its decision.
    if (this.props.onClose) {
      if (e?.preventDefault) e.preventDefault();
      this.props.onClose();
      return;
    }

    if (window.history.length === 1) {
      window.location.href = "/about/committee";
    }

    window.history.back();
  }

  isAllowed() {
    let user = this.props.currentUser;
    let isOfficer = user.Role.permission_level >= PermissionLevel.Officer;
    let isUser = user.user_id === this.state.profile.id;
    return isOfficer || isUser;
  }

  renderOfficerRoles() {
    let userPermissions = this.state.profile.permission_level;
    let isCommitteeMember = userPermissions >= PermissionLevel.Committee;
    
    if (!isCommitteeMember) {
      return null;
    }

    const { officerRoles, selectedOfficerRoleIds } = this.state;

    if (officerRoles.length === 0) {
      return <p className="clr:text-secondary-base">No officer roles available</p>;
    }

    // Sort officer roles by sort_order
    const sortedRoles = [...officerRoles].sort((a, b) => a.sortOrder - b.sortOrder);

    return (
      <div
        className="officer-roles-list"
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: '0.25rem',
          padding: '0.5rem',
        }}
      >
        {sortedRoles.map((role) => {
          const isChecked = selectedOfficerRoleIds.includes(role.id);
          return (
            <PrismCheckbox
              key={role.id}
              name={`officer-role-${role.id}`}
              label={role.name}
              checked={isChecked}
              onPrismChange={onCheckedChange(isChecked, () => this.handleOfficerRoleChange(role.id))}
            />
          );
        })}
      </div>
    );
  }

  render() {
    if (!this.isAllowed()) {
      return <Unauthorized />;
    }

    if (this.state.loading) {
      return <Loader />;
    }

    let alert = null;
    if (this.state.error) {
      alert = (
        <PrismBanner tone="danger" layout="inline">
          {this.state.error}
        </PrismBanner>
      );
    }

    if (this.state.success && !this.props.embedded) {
      alert = (
        <PrismBanner tone="success" layout="inline">
          Profile Updated!{" "}
          <a href="#" onClick={this.handleCancel}>
            Go Back <FontAwesomeIcon icon={faChevronRight} />
          </a>
        </PrismBanner>
      );
    }

    let profile = this.state.profile;
    const form = (
      <form
        key={profile.id}
        id="profile-form"
        onSubmit={this.handleSubmit}
        className="form-container"
      >
        <h2>{profile.preferred_name || profile.name}</h2>
        {alert}
        <div className="form-group">
          <PrismInputText
            name="preferred_name"
            label="Preferred Name"
            value={profile.preferred_name || ''}
            onPrismChange={this.handlePrismChange('preferred_name')}
          />
        </div>
        <div className="form-group">
          <label className="form-group-label">Officer Roles</label>
          {this.renderOfficerRoles()}
          <small className="form-group-help">
            Select all officer roles that apply to this user
          </small>
        </div>
        <FileInput object={profile} fileKey="photo" entityType="Photo" allowedTypes={['jpeg', 'jpg', 'png']} imageVariant="" invalidFileTypeCallback={this.invalidFileSelected} />
        <div className="form-group">
          <PrismInputTextarea
            name="bio"
            label="Bio"
            value={profile.bio || ''}
            rows={5}
            onPrismChange={this.handlePrismChange('bio')}
          />
        </div>
        <div className="profile-form-actions">
          <PrismButton
            tone="primary"
            type="submit"
          >
            Save Item
          </PrismButton>
          <PrismButton
            tone="secondary"
            display="outline"
            onPrismClick={this.handleCancel}
          >
            Cancel
          </PrismButton>
        </div>
      </form>
    );

    // Embedded hosts supply their own chrome (a dialog header and the top-layer
    // backdrop), so the page wrapper would nest a <main> and a second title.
    if (this.props.embedded) {
      return form;
    }

    return (
      <main>
        <PageHeader title="Edit Profile" />
        {form}
      </main>
    );
  }
}
